import { useEffect, useState } from 'react';
import { useSelector, selectSubmittedTokens } from '@splunk/dashboard-state';
import {
    asyncJsonataEvaluation,
    type AnyEvaluationResult,
    type EvaluationResultValue,
} from '@splunk/dashboard-utils';

/** Asynchronously evaluate a JSONata expression with resolved tokens.
 *
 * If provided, an initial assumed result can be returned immediately. By default this initial assumed result will be `null`\
 * If the initial result is an instance of `Error` the assumed result will be an error state (`{}.success = false`)
 */
export const useJsonataEvaluation = (
    expression: string,
    initialValue: EvaluationResultValue | Error = null
): AnyEvaluationResult => {
    const [result, setResult] = useState<AnyEvaluationResult>(() => {
        if (initialValue instanceof Error) {
            return { success: false, reason: initialValue.message };
        }

        return { success: true, value: initialValue };
    });

    const tokens = useSelector(selectSubmittedTokens);
    useEffect(() => {
        const abortController = new AbortController();
        (async () => {
            const asyncEvalResult = await asyncJsonataEvaluation({
                expression,
                tokens,
            });

            if (abortController.signal.aborted) {
                return;
            }

            setResult(asyncEvalResult);
        })();

        return () => {
            abortController.abort();
        };
    }, [expression, tokens]);

    return result;
};
